// @ts-strict-ignore
import { useCallback, useState } from 'react';
import type { Location as RouterLocation } from 'react-router';
import env from '@/env';
import { API_URL } from './url';
import useIsMountedRef from './useIsMountedRef';

export const LOGIN_URL = env.LOGIN_URL || 'https://login.binosight.com';
export const CLIENT_ID = env.CLIENT_ID || window.location.hostname;

// const storage = window.localStorage; // Provides the best experience. Vulnerable to XSS
// const storage = window.sessionStorage; // Provides good experience. Vulnerable to XSS. New tab => new token

const storage = {
    // Provides ok experience. New tab => new token
    _: {},
    setItem(key: string, value: unknown) {
        storage._[key] = value;
    },
    getItem(key: string) {
        return storage._[key];
    },
    removeItem(key: string) {
        delete storage._[key];
    },
};

export function receiveAccessToken(token: string, isMagic?: boolean) {
    storage.setItem('accessToken', token);
    storage.setItem('magic', isMagic ? 'true' : 'false');
}

export function currentAccessToken() {
    return storage.getItem('accessToken');
}

export function isMagicAccessToken() {
    return storage.getItem('magic') === 'true';
}

export function hasAccessToken() {
    return (
        !!currentAccessToken() &&
        'undefined' !== currentAccessToken() &&
        'null' !== currentAccessToken()
    );
}

export function manageAccountUrl(currentLocation: Location) {
    return `${LOGIN_URL}/manage/account?redirect_uri=${encodeURIComponent(
        currentLocation.href,
    )}&client_id=${CLIENT_ID}`;
}

export function deleteAccessToken() {
    storage.removeItem('accessToken');
    storage.removeItem('magic');
}

export function getTokenCallbackUrl(next = '/') {
    return baseUrl() + '/oauth/token-callback?next=' + encodeURIComponent(next);
}

/**
 * Gets the base url from the window location
 *
 * todo move this to a URL utilities package
 */
function baseUrl(location: Location = window.location) {
    const { protocol, hostname, port } = location;
    return protocol + '//' + hostname + (port ? ':' : '') + port;
}

export function generateLoginUrl(
    currentLocationOrNext: string | Location | RouterLocation,
    fullUrl?: boolean,
) {
    const loginUri = '/login';
    let next: string;
    if (typeof currentLocationOrNext === 'string') {
        next = currentLocationOrNext;
    } else {
        const { pathname, search, hash } = currentLocationOrNext;
        if (pathname.startsWith(loginUri)) {
            return loginUri;
        }
        next = pathname + search + hash;
    }
    return (
        (fullUrl ? baseUrl() : '') +
        loginUri +
        (next.length > 1 && next !== '/'
            ? `?next=${encodeURIComponent(next)}`
            : '')
    );
}

export function generateLoginWithMicrosoftUrl(
    currentLocationOrNext: string | Location | RouterLocation,
    email?: string,
) {
    let next = '/';
    if (typeof currentLocationOrNext === 'string') {
        next = currentLocationOrNext;
    } else {
        const { pathname, search, hash } = currentLocationOrNext;
        if (!pathname.startsWith('/login')) {
            next = pathname + search + hash;
        }
    }
    const search = new URLSearchParams({
        client_id: CLIENT_ID,
        next,
        email,
    });
    //next = encodeURIComponent(`${baseUrl()}/login/cookie?next=${next}`);
    return `${API_URL}/service/login/oidc?${search}`;
}

export function generateLoginWithSSOUrl(
    currentLocationOrNext: string | Location | RouterLocation,
) {
    let next = '/';
    if (typeof currentLocationOrNext === 'string') {
        next = currentLocationOrNext;
    } else {
        const { pathname, search, hash } = currentLocationOrNext;
        if (!pathname.startsWith('/login')) {
            next = pathname + search + hash;
        }
    }
    //next = encodeURIComponent(`${baseUrl()}/login/cookie?next=${next}`);
    return `${API_URL}/service/login/saml/login?client_id=${CLIENT_ID}&next=${next}`;
}

export enum LoginState {
    Idle,
    InvalidPassword,
    UnknownServerError,
    LoggingIn,
    LoggedIn,
}

export type LoginWithPasswordFn = (
    username: string,
    password: string,
) => Promise<void>;
export type ResetLoginStateFn = () => void;

export function useLoginWithPassword(
    next = '/',
    onSuccess?: (user: {
        id: string;
        fullName: string;
        firstName: string;
        lastName: string;
        emailAddress: string;
    }) => void,
): [LoginState, LoginWithPasswordFn, ResetLoginStateFn] {
    const [state, setState] = useState(LoginState.Idle);
    const isMountedRef = useIsMountedRef();

    return [
        state,
        useCallback(
            async (username: string, password: string) => {
                if (
                    state === LoginState.LoggingIn ||
                    state === LoginState.LoggedIn
                ) {
                    return;
                }
                setState(LoginState.LoggingIn);
                try {
                    const response = await fetch(
                        `${API_URL}/service/login/login.cookie`,
                        {
                            method: 'POST',
                            credentials: 'include',
                            headers: {
                                'Content-Type': 'application/json',
                                Accept: 'application/json',
                            },
                            body: JSON.stringify({
                                client_id: CLIENT_ID,
                                username,
                                password,
                            }),
                        },
                    );
                    if (!isMountedRef.current) {
                        return;
                    }
                    if (response.status === 200) {
                        // Not really needed since we do window.location.href = next.
                        // Is this a good pattern? Maybe leave this up to the user to
                        // use an effect here instead of redirecting
                        setState(LoginState.LoggedIn);

                        // todo Send signal to other tabs that we are logged in
                        if (onSuccess) {
                            const data = await response.json();
                            onSuccess(data.user);
                        } else {
                            window.location.href = next;
                        }
                    } else {
                        // todo perhaps check the response for a more exact message. This is very likely though
                        setState(LoginState.InvalidPassword);
                    }
                } catch (e) {
                    if (isMountedRef.current) {
                        console.error(e);
                        setState(LoginState.UnknownServerError);
                    }
                }
            },
            [isMountedRef, state, next, onSuccess],
        ),
        useCallback(() => {
            setState(LoginState.Idle);
        }, []),
    ];
}

type LogoutListener = () => void;
const logoutListeners = [] as Array<LogoutListener>;

export function addLogoutListener(listener: LogoutListener): void {
    logoutListeners.push(listener);
}

export enum LogoutState {
    Idle,
    Failed,
    LoggingOut,
    LoggedOut,
}

export function useLogout(next = '/login'): [LogoutState, () => void] {
    const [state, setState] = useState<LogoutState>(LogoutState.Idle);
    const isMountedRef = useIsMountedRef();

    return [
        state,
        useCallback(() => {
            if (
                state === LogoutState.LoggingOut ||
                state === LogoutState.LoggedOut
            ) {
                return;
            }
            setState(LogoutState.LoggingOut);

            fetch(API_URL + '/service/login/logout.cookie', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify({
                    client_id: CLIENT_ID,
                }),
            })
                .then(() => {
                    logoutListeners.forEach((listener) => listener());
                    setState(LogoutState.Idle);
                    if (isMountedRef.current) {
                        // todo send signal to other tabs to reload
                        window.location.href = next;
                    }
                })
                .catch((e) => {
                    console.error(e);
                    setState(LogoutState.Failed);
                });
        }, [isMountedRef, state, next]),
    ];
}
